import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Starfield from "@/components/Starfield";
import Rocket from "@/components/Rocket";
import { teacherRegister, teacherLogin, pupilLogin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { GraduationCap, UserRound, X, Loader2 } from "lucide-react";

const pupilKey = (classId) => `starlight_pupil_${classId}`;

export default function Join() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [modal, setModal] = useState(null); // 'teacher' | 'new' | 'return' | 'pupil'
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [newCode, setNewCode] = useState(null); // teacher code to reveal after register

  const enterClass = () => {
    const c = code.trim().toUpperCase();
    if (!c) return toast.error("Type your class mission code");
    navigate(`/play/${encodeURIComponent(c)}`);
  };

  const close = () => { setModal(null); setName(""); setPin(""); setErr(""); setBusy(false); setNewCode(null); };

  const doRegister = async () => {
    if (!name.trim()) return setErr("Please enter your name");
    setBusy(true); setErr("");
    try {
      const d = await teacherRegister(name.trim());
      setNewCode(d.code); // reveal code, then go to dashboard
    } catch { setErr("Could not create your account. Try again."); }
    finally { setBusy(false); }
  };

  const doTeacherLogin = async () => {
    if (!pin.trim()) return setErr("Enter your teacher code");
    setBusy(true); setErr("");
    try {
      await teacherLogin(pin.trim());
      close();
      navigate("/teacher");
    } catch (e) {
      setErr(e?.response?.status === 429 ? "Too many attempts — wait a few minutes." : "No teacher found for that code.");
    } finally { setBusy(false); }
  };

  const doPupilLogin = async () => {
    if (!pin.trim()) return setErr("Enter your pupil code");
    setBusy(true); setErr("");
    try {
      const d = await pupilLogin(pin.trim());
      localStorage.setItem(pupilKey(d.classId), d.pupilId);
      close();
      navigate(`/play/${encodeURIComponent(d.classCode)}`);
    } catch (e) {
      setErr(e?.response?.status === 429 ? "Too many attempts — wait a few minutes." : "No pupil found for that code.");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden flex items-center justify-center p-4">
      <Starfield />
      <div className="relative z-10 w-full max-w-md text-center">
        <div className="rocket-float inline-block mb-3"><Rocket color="#00F0FF" size={72} /></div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-1">
          Stellar <span className="text-cyan-300">Steps</span>
        </h1>
        <p className="text-slate-400 mb-7">Enter your class mission code to blast off 🚀</p>

        <div className="bg-[#141738] border border-[#252B5C] rounded-3xl p-6">
          <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Class mission code</label>
          <Input
            data-testid="join-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && enterClass()}
            placeholder="e.g. 6BKCD"
            className="bg-[#0E1126] border-[#252B5C] text-white text-center text-3xl font-heading tracking-[0.3em] min-h-[64px] mt-1 mb-4"
            autoFocus
          />
          <Button
            data-testid="join-enter-btn"
            onClick={enterClass}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[52px] text-lg"
          >
            Enter mission 🚀
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <Button
            data-testid="teacher-btn"
            onClick={() => setModal("teacher")}
            variant="outline"
            className="border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10 min-h-[48px]"
          >
            <GraduationCap className="w-4 h-4 mr-2" /> Teacher
          </Button>
          <Button
            data-testid="returning-pupil-btn"
            onClick={() => setModal("pupil")}
            variant="outline"
            className="border-cyan-400/40 bg-transparent text-cyan-200 hover:bg-[#1C204B] min-h-[48px]"
          >
            <UserRound className="w-4 h-4 mr-2" /> Returning Pupil
          </Button>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div
            data-testid="auth-modal"
            className="relative w-full max-w-sm bg-[#141738] border border-[#252B5C] rounded-3xl p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={close} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>

            {modal === "teacher" && (
              <>
                <h2 className="font-heading text-2xl font-bold text-amber-300 mb-4">Are you a…</h2>
                <div className="flex flex-col gap-3">
                  <Button data-testid="teacher-new-btn" onClick={() => { setModal("new"); setErr(""); }} className="bg-gradient-to-r from-amber-500 to-pink-500 text-white font-heading font-bold min-h-[52px]">New teacher</Button>
                  <Button data-testid="teacher-returning-btn" onClick={() => { setModal("return"); setErr(""); }} variant="outline" className="border-amber-400/40 bg-transparent text-amber-200 hover:bg-amber-400/10 min-h-[52px]">Returning teacher</Button>
                </div>
              </>
            )}

            {modal === "new" && !newCode && (
              <>
                <h2 className="font-heading text-2xl font-bold text-amber-300 mb-1">New teacher</h2>
                <p className="text-slate-400 text-sm mb-4">Enter your name — we'll give you a personal 6-digit code to log back in.</p>
                <Input data-testid="teacher-name-input" value={name} onChange={(e) => { setName(e.target.value); setErr(""); }} onKeyDown={(e) => e.key === "Enter" && doRegister()} placeholder="e.g. Mr. Lewis" className="bg-[#0E1126] border-[#252B5C] text-white min-h-[52px] text-lg mb-2" autoFocus />
                {err && <p data-testid="auth-error" className="text-red-400 text-sm mb-2">{err}</p>}
                <Button data-testid="teacher-register-submit" onClick={doRegister} disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create my account"}</Button>
              </>
            )}

            {modal === "new" && newCode && (
              <>
                <h2 className="font-heading text-2xl font-bold text-amber-300 mb-1">Your teacher code</h2>
                <p className="text-slate-400 text-sm mb-3">Keep it safe — use it to log back in from any device.</p>
                <div data-testid="teacher-code-value" className="font-heading font-bold text-5xl tracking-[0.3em] text-amber-300 bg-[#0E1126] border-2 border-amber-400/40 rounded-2xl py-6 mb-4">{newCode}</div>
                <Button data-testid="teacher-code-continue" onClick={() => { close(); navigate("/teacher"); }} className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]">Go to my classes</Button>
              </>
            )}

            {modal === "return" && (
              <>
                <h2 className="font-heading text-2xl font-bold text-amber-300 mb-1">Returning teacher</h2>
                <p className="text-slate-400 text-sm mb-4">Enter your 6-digit teacher code.</p>
                <Input data-testid="teacher-code-input" value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setErr(""); }} onKeyDown={(e) => e.key === "Enter" && doTeacherLogin()} inputMode="numeric" placeholder="••••••" className="bg-[#0E1126] border-[#252B5C] text-white text-center text-3xl font-heading tracking-[0.4em] min-h-[60px] mb-2" autoFocus />
                {err && <p data-testid="auth-error" className="text-red-400 text-sm mb-2">{err}</p>}
                <Button data-testid="teacher-login-submit" onClick={doTeacherLogin} disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open my classes"}</Button>
              </>
            )}

            {modal === "pupil" && (
              <>
                <h2 className="font-heading text-2xl font-bold text-cyan-300 mb-1">Welcome back!</h2>
                <p className="text-slate-400 text-sm mb-4">Enter your 6-digit pupil code to hop back into your mission.</p>
                <Input data-testid="pupil-code-input" value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setErr(""); }} onKeyDown={(e) => e.key === "Enter" && doPupilLogin()} inputMode="numeric" placeholder="••••••" className="bg-[#0E1126] border-[#252B5C] text-white text-center text-3xl font-heading tracking-[0.4em] min-h-[60px] mb-2" autoFocus />
                {err && <p data-testid="auth-error" className="text-red-400 text-sm mb-2">{err}</p>}
                <Button data-testid="pupil-login-submit" onClick={doPupilLogin} disabled={busy} className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter my mission 🚀"}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
